/**
 * アコーディオン（data 属性駆動）
 *
 * マークアップ規約:
 *   <div data-lms-accordion data-open>            ← data-open が付いている間だけ開いた状態
 *     <button data-lms-accordion-trigger>…</button>
 *     <div data-lms-accordion-panel>…</div>
 *   </div>
 *
 * ネスト可能（サイドバーの chapter → sub → section の 3 階層で使う）。
 * トリガーは直近の [data-lms-accordion] を探し、その「直下の」パネルだけを開閉するため、
 * 入れ子のアコーディオンが誤って一緒に開くことはない。
 */

/** 指定要素の直下にあるパネルを返す（孫要素のパネルは対象外） */
function findPanel(root: HTMLElement): HTMLElement | null {
  for (const child of Array.from(root.children)) {
    if (child instanceof HTMLElement && child.hasAttribute("data-lms-accordion-panel")) {
      return child;
    }
  }
  return null;
}

function applyState(root: HTMLElement, trigger: HTMLElement, panel: HTMLElement, open: boolean): void {
  if (open) {
    root.setAttribute("data-open", "");
    panel.hidden = false;
    // 開いたときだけアニメーションを再生させる
    panel.style.animation = "";
  } else {
    root.removeAttribute("data-open");
    panel.hidden = true;
  }
  trigger.setAttribute("aria-expanded", String(open));
}

export function initAccordion(scope: ParentNode = document): void {
  scope.querySelectorAll<HTMLElement>("[data-lms-accordion]").forEach((root) => {
    if (root.dataset["lmsAccordionReady"] === "1") return;

    const trigger = root.querySelector<HTMLElement>("[data-lms-accordion-trigger]");
    const panel = findPanel(root);
    if (!trigger || !panel) return;

    root.dataset["lmsAccordionReady"] = "1";

    // パネルに id が無ければ生成し、aria-controls で関連付ける
    if (!panel.id) {
      panel.id = `lms-acc-${Math.random().toString(36).slice(2, 10)}`;
    }
    trigger.setAttribute("aria-controls", panel.id);
    if (trigger instanceof HTMLButtonElement) {
      trigger.type = "button";
    } else {
      // button 要素でない場合もキーボード操作できるようにする
      trigger.setAttribute("role", "button");
      trigger.tabIndex = 0;
    }

    applyState(root, trigger, panel, root.hasAttribute("data-open"));

    const toggle = (): void => {
      applyState(root, trigger, panel, !root.hasAttribute("data-open"));
    };

    trigger.addEventListener("click", (e) => {
      e.preventDefault();
      toggle();
    });

    // role=button で代用している場合の Enter / Space 対応
    if (!(trigger instanceof HTMLButtonElement)) {
      trigger.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggle();
        }
      });
    }
  });
}
